<template>
   <RequestLoader :isLoading="state.isFetchingData" data-cy="profile-dob-block" style="min-height: 6.75rem">
      <div class="grid grid-cols-3 gap-2 pt-4" data-cy="profile-dob-v1">
         <!-- v-if used to define order of inputs (isFormatDDMMYY) -->
         <NumberTextBox
            v-if="isFormatDDMMYY"
            v-model.trim="state.input.day"
            :placeholder="`DD`"
            :hasError="showBlockErrors && (inputHasError(period.day) || inputHasError('date'))"
            :label="$t('validation.date.day.period')"
            :maxLength="2"
            @change="processInputs"
         />
         <NumberTextBox
            v-model.trim="state.input.month"
            :placeholder="`MM`"
            :hasError="showBlockErrors && inputHasError(period.month)"
            :label="$t('validation.date.month.period')"
            :maxLength="2"
            @change="processInputs"
         />
         <NumberTextBox
            v-if="!isFormatDDMMYY"
            v-model.trim="state.input.day"
            :placeholder="`DD`"
            :hasError="showBlockErrors && (inputHasError(period.day) || inputHasError('date'))"
            :label="$t('validation.date.day.period')"
            :maxLength="2"
            @change="processInputs"
         />
         <NumberTextBox
            v-model.trim="state.input.year"
            :placeholder="`YYYY`"
            :hasError="showBlockErrors && inputHasError(period.year)"
            :label="$t('validation.date.year.period')"
            :maxLength="4"
            @change="processInputs"
         />
      </div>
      <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
   </RequestLoader>
</template>

<script setup lang="ts">
/*
   Block for capturing Date of birth.
   - why are we using text input for numbers?
   https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
*/
import useVuelidate from "@vuelidate/core";
import { requiredIf, numeric, maxValue, minValue, minLength, helpers } from "@vuelidate/validators";
import { useI18n } from "@/vue-i18n/vue-i18n";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { getDateInputFromFormatted, getFormattedFromDateInput, isDateReal } from "../date/date-utils";
import { useUserStore } from "@/stores/userStore";

export interface ProfileDateOfBirthAnswerData {
   date: string;
}

export interface ProfileDateOfBirthBlock extends FlowselBlock {
   data: {
      displayFormat: "dd-mm-yyyy" | "yyyy-mm-dd";
   };
}

interface Props {
   block: ProfileDateOfBirthBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const { t: $t } = useI18n();
const props = defineProps<Props>();
const DATE_FORMAT = "YYYY-MM-DD";
dayjs.extend(customParseFormat);
const emit = defineEmits(["update-valid", "update-answer"]);
const userStore = useUserStore();

const period = {
   day: "day",
   month: "month",
   year: "year",
};
const today = dayjs();
const thisYear = today.year();
const thisMonth = today.month() + 1; // dayjs months are 0-indexed
const thisDay = today.date();

const state = reactive({
   isFetchingData: false,
   input: {
      day: "",
      month: "",
      year: "",
   },
   requiredDateFormat: unref(props.block.data.displayFormat),
});

// Vuelidate & validation
// -----------------------------------------
const rules = {
   input: {
      date: {
         realDate: helpers.withMessage(
            () => $t("validation.date.invalid_date"),
            () => isDateReal(state.input.day, state.input.month, state.input.year)
         ),
      },
      year: {
         required: helpers.withMessage(() => $t("validation.date.required"), requiredIf(props.block.isRequired)),
         minValue: helpers.withMessage(() => $t("validation.date.year.invalid_min"), minValue(thisYear - 150)),
         minLength: helpers.withMessage(() => $t("validation.date.year.invalid_length"), minLength(4)),
         pastDateYear: helpers.withMessage(
            () => $t("validation.date_of_birth.invalid_max"),
            (year: string) => {
               return Number(year) <= thisYear;
            }
         ),
         numeric: helpers.withMessage(() => $t("validation.date.invalid_type"), numeric),
         $autoDirty: true,
      },
      month: {
         required: helpers.withMessage(() => $t("validation.date.required"), requiredIf(props.block.isRequired)),
         minValue: helpers.withMessage(() => $t("validation.date.month.invalid_min"), minValue(1)),
         maxValue: helpers.withMessage(() => $t("validation.date.month.invalid_max"), maxValue(12)),
         pastDateMonth: helpers.withMessage(
            () => $t("validation.date_of_birth.invalid_max"),
            (month: string) => {
               if (Number(state.input.year) === thisYear) {
                  return Number(month) <= thisMonth;
               }
               return true;
            }
         ),
         numeric: helpers.withMessage(() => $t("validation.date.invalid_type"), numeric),
         $autoDirty: true,
      },
      day: {
         required: helpers.withMessage(() => $t("validation.date.required"), requiredIf(props.block.isRequired)),
         minValue: helpers.withMessage(() => $t("validation.date.day.invalid_min"), minValue(1)),
         maxValue: helpers.withMessage(() => $t("validation.date.day.invalid_max"), maxValue(31)),
         pastDateDay: helpers.withMessage(
            () => $t("validation.date_of_birth.invalid_max"),
            (day: string) => {
               if (Number(state.input.year) === thisYear && Number(state.input.month) === thisMonth) {
                  return Number(day) <= thisDay;
               }
               return true;
            }
         ),
         numeric: helpers.withMessage(() => $t("validation.date.invalid_type"), numeric),
         $autoDirty: true,
      },
   },
};

const $v: any = useVuelidate(rules, state);

const isFormatDDMMYY = computed(() => state.requiredDateFormat === "dd-mm-yyyy");

const errorList = computed(() => {
   const allErrors = $v.value.input.$errors.map((vError) => {
      return {
         property: vError.$property,
         inputId: props.block.id,
         validator: vError.$validator,
         message: vError.$message,
      };
   });
   // only show max of 1 error
   const lastErrorOnly = allErrors.slice(allErrors.length - 1);
   return lastErrorOnly;
});

// Methods
// -----------------------------------------
/*
   Profile Block
   If the UserBlockAnswer answer is NULL, show loader (no inputs) and try to fetch any user profile data (e.g. another flow answer) to populate fields
   If the block answer is NOT NULL, use this to try to populate fields
*/

/**
 * Check userBlockAnswer for a previous response before
 * attempting to use user data to pre-populate
 */
function checkUserBlockAnswer() {
   if (props.userBlockAnswer?.data?.date) {
      initialiseComponent(props.userBlockAnswer.data.date);
   } else {
      state.isFetchingData = true;
      fetchUserData().finally(() => (state.isFetchingData = false));
   }
}

/**
 * Try and fetch user data that we might be able to use to pre-populate fields with
 */
async function fetchUserData() {
   try {
      await userStore.setUser();
      initialiseComponent(userStore.user?.birthday);
   } catch (e) {
      console.error("Failed to fetch user data", e);
   }
}

/**
 * update the inputs with any retrieved profile data
 * @param {object} userData
 */
function initialiseComponent(formattedDate: string) {
   if (formattedDate) {
      state.input = getDateInputFromFormatted(formattedDate, DATE_FORMAT);
   }
   processInputs();
}

function inputHasError(inputKey) {
   const propertiesWithErrors = $v.value.input.$errors.map((item) => item.$property);
   return propertiesWithErrors.includes(inputKey);
}

function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   if ($v.value.$invalid || $v.value.$error) {
      // an input is invalid (some missing required fields / has errors)
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      let answerToSubmit = null;
      if (state.input.year && state.input.month && state.input.day) {
         answerToSubmit = { date: getFormattedFromDateInput(state.input, DATE_FORMAT) };
      }
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

// On load, check for an existing answer or for user data
checkUserBlockAnswer();
</script>
